import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  SparklesIcon,
  ArrowRightIcon,
  ChevronRightIcon,
  RocketLaunchIcon,
  PlayCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { companyApi } from '../../services/api';
import ConfirmDialog from '../ConfirmDialog';

const STEP_NAMES = ['项目配置', '招标解析', '分项报价', '目录生成', '文件生成'];

const HeroSection: React.FC = () => {
  const navigate = useNavigate();
  const [hasCompany, setHasCompany] = useState<boolean | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);

  // 视频教程链接
  const videoUrl = process.env.REACT_APP_TUTORIAL_VIDEO_URL || '';

  // 检查用户是否有主体
  useEffect(() => {
    const checkCompany = async () => {
      try {
        const response = await companyApi.getCompanies();
        setHasCompany(response.data && response.data.length > 0);
      } catch (e) {
        setHasCompany(false);
      }
    };
    checkCompany();
  }, []);

  const handleCreateProject = () => {
    if (hasCompany === false) {
      setShowConfirm(true);
    } else {
      navigate('/create');
    }
  };

  return (
    <section
      className="relative min-h-screen flex flex-col justify-center overflow-hidden"
      style={{ background: 'linear-gradient(to bottom, #1e3a5f 0%, #2d5aa0 60%, #ffffff 100%)' }}
    >
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute rounded-full"
          style={{
            top: '15%', left: '5%',
            width: '600px', height: '600px',
            background: 'radial-gradient(circle, rgba(59,130,246,0.18) 0%, transparent 70%)',
          }}
        />
        <div
          className="absolute rounded-full"
          style={{
            bottom: '20%', right: '5%',
            width: '500px', height: '500px',
            background: 'radial-gradient(circle, rgba(6,182,212,0.12) 0%, transparent 70%)',
          }}
        />
        <div
          className="absolute rounded-full"
          style={{
            top: '55%', left: '45%',
            width: '350px', height: '350px',
            background: 'radial-gradient(circle, rgba(37,99,235,0.1) 0%, transparent 70%)',
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `
              linear-gradient(to right, rgba(255,255,255,0.12) 1px, transparent 1px),
              linear-gradient(to bottom, rgba(255,255,255,0.12) 1px, transparent 1px)
            `,
            backgroundSize: '72px 72px',
          }}
        />
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-6 pt-28 pb-20 text-center">
        <div
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full border mb-10"
          style={{
            background: 'rgba(59, 130, 246, 0.1)',
            borderColor: 'rgba(59, 130, 246, 0.3)',
          }}
        >
          <SparklesIcon className="w-3.5 h-3.5 text-blue-400" />
          <span className="text-sm font-medium text-blue-300">AI 驱动 · 智能标书助手</span>
          <span
            className="ml-1 px-1.5 py-0.5 text-[10px] font-bold rounded"
            style={{ background: 'rgba(59, 130, 246, 0.3)', color: '#93c5fd' }}
          >
            v1.0
          </span>
        </div>

        <h1 className="text-5xl md:text-[72px] font-black text-white mb-8 leading-[1.1] tracking-tight">
          让标书编写
          <br />
          <span
            className="bg-clip-text text-transparent"
            style={{
              backgroundImage: 'linear-gradient(90deg, #60a5fa 0%, #38bdf8 50%, #22d3ee 100%)',
            }}
          >
            更高效、更智能
          </span>
        </h1>

        <p className="text-xl text-slate-400 max-w-2xl mx-auto mb-14 leading-relaxed">
          一站式 AI 辅助生成，
          <span className="text-slate-200 font-medium">智能 5 步</span>，
          大幅提升标书交付效率与专业品质
        </p>

        <div className="flex flex-wrap items-center justify-center gap-4 mb-20">
          <button
            onClick={handleCreateProject}
            className="inline-flex items-center gap-2.5 px-8 py-4 text-white font-bold text-base rounded-2xl transition-all duration-200 hover:scale-[1.03] hover:brightness-110"
            style={{
              background: 'linear-gradient(135deg, #2563eb, #0284c7)',
              boxShadow: '0 8px 32px rgba(37, 99, 235, 0.45)',
            }}
          >
            <RocketLaunchIcon className="w-5 h-5" />
            立即新建标书
          </button>
          {videoUrl && (
            <button
              onClick={() => setShowVideoModal(true)}
              className="inline-flex items-center gap-2 px-8 py-4 text-white font-medium text-base rounded-2xl border border-slate-400 bg-white/5 transition-all duration-200 hover:scale-[1.02]"
            >
              <PlayCircleIcon className="w-5 h-5" />
              观看视频教程
            </button>
          )}
          <button
            onClick={() => document.getElementById('steps')?.scrollIntoView({ behavior: 'smooth' })}
            className="inline-flex items-center gap-2 px-8 py-4 text-white font-medium text-base rounded-2xl border border-slate-400 bg-white/5 transition-all duration-200 hover:scale-[1.02]"
          >
            了解创建流程
            <ArrowRightIcon className="w-4 h-4" />
          </button>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-2">
          {STEP_NAMES.map((name, i) => (
            <React.Fragment key={name}>
              <div
                className="flex items-center gap-2 px-3.5 py-2 rounded-xl border"
                style={{
                  background: 'rgba(30, 58, 95, 0.6)',
                  borderColor: 'rgba(255,255,255,0.2)',
                }}
              >
                <span
                  className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0"
                  style={{ background: '#2563eb' }}
                >
                  {i + 1}
                </span>
                <span className="text-white text-sm whitespace-nowrap">{name}</span>
              </div>
              {i < STEP_NAMES.length - 1 && (
                <ChevronRightIcon className="w-4 h-4 text-slate-600 flex-shrink-0" />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* 主体缺失确认弹框 */}
      <ConfirmDialog
        isOpen={showConfirm}
        title="需要设置投标主体"
        message="创建标书需要先设置投标主体信息。您当前还没有主体信息，建议先创建主体后再进行标书创建。"
        confirmText="去创建主体"
        cancelText="去创建标书"
        confirmButtonClass="bg-blue-600 hover:bg-blue-700 focus:ring-blue-500"
        onConfirm={() => {
          setShowConfirm(false);
          navigate('/companies/new');
        }}
        onCancel={() => {
          setShowConfirm(false);
          navigate('/create');
        }}
      />

      {/* 视频教程弹框 */}
      {showVideoModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          {/* 背景遮罩 */}
          <div
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={() => setShowVideoModal(false)}
          />
          {/* 视频容器 */}
          <div className="relative w-full max-w-5xl bg-black rounded-2xl overflow-hidden shadow-2xl">
            {/* 关闭按钮 */}
            <button
              onClick={() => setShowVideoModal(false)}
              className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
            {/* 视频播放器 */}
            <video
              src={videoUrl}
              controls
              autoPlay
              className="w-full aspect-video"
              style={{ maxHeight: '80vh' }}
            >
              您的浏览器不支持视频播放
            </video>
          </div>
        </div>
      )}
    </section>
  );
};

export default HeroSection;
